<script lang="ts" setup>
import AUApi from "@/api";
import { ref, onMounted, reactive } from "vue";
import { isPhone } from "@/DeviceInfoPlugin";
import { Globals } from "@/Globals";
import { numberToStringWithThousends, toNumber } from "@/DataHelper";
import { showPDF2, showImage } from "@/PdfHelper";
import { DialogParameter, showDialog } from "@/UIHelper";
import Constants from "@/Constants";
import {
  getKZValueDisplay,
  getKennungValue,
  getKZTitle,
  getKZValue,
} from "@/DataHelper";
import { globalsBezeichnung, globalsKennung } from "@/local";
import {
  mountedBase,
  BaseSetup,
  reloadData,
  onDelete,
} from "@/DataLoaderStatic";
import { getSiteData } from "@/NavigationHelper";
const emit = defineEmits<{
  back: [];
  onReloadData: [data?: any];
  calculated: [data?: any];
}>();
const props = defineProps<{
  contentHeight?: number;
  edit?: boolean;
  aic: number;
  zeitbereichUebergabe: APIZeitbereich;
  query: string;
}>();

let editMask: string = "RkMitarbeiterEditKostenersatz";
let innerHeight: number = 100;
let sumCost = ref<number>(0);
let sumCost1 = ref<string>("");
let itemHeight = ref<string>("84");
let items = ref<ListItems[]>([]);
let heightLocal = ref<number>(props.contentHeight ?? innerHeight);
let belegeAzure = ref<boolean>(false);
const pageParams: DataPageParameter = {
  zeitbereich: AUApi.Current.zeitbereich,
  insertMode: false,
  emitBack: () => emit("back"),
};
const uiData = reactive<DataPageUIData>(BaseSetup(pageParams));
onMounted(async () => {
  const siteData = getSiteData();
  if (siteData?.editAzureBeleg) {
    belegeAzure.value = siteData?.editAzureBeleg;
  }
  pageParams.zeitbereich = props.zeitbereichUebergabe;
  pageParams.queryAIC = props.aic;
  pageParams.query = props.query;
  await loadData();
});
async function loadData() {
  await mountedBase(uiData, pageParams, false);
  if (!uiData.showLoading && uiData.data) {
    refreshItems();
  }

  uiData.showLoading = false;
  uiData.initialized = true;
}
async function refreshItems() {
  items.value = []; // ausleeren für refresh
  sumCost.value = 0;

  for (let i = 0; i < uiData.data.length; i++) {
    const row = uiData.data[i];
    const header = getKZValueDisplay(row, "header", uiData.columns);
    const differenz: any = getKennungValue(row, "hours", uiData.columns);
    const title = getKZValueDisplay(row, "titel", uiData.columns);
    let title2 = getKZValueDisplay(row, "titel2", uiData.columns);
    if (title2) {
      const textTitle2 = getKZTitle(row, "titel2", uiData.columns);
      title2 = textTitle2 + ": " + title2;
    }
    let subtitel = getKZValueDisplay(row, "subtitel", uiData.columns);
    if (subtitel) {
      const textSubtitel = getKZTitle(row, "subtitel", uiData.columns);
      subtitel = textSubtitel + ": " + subtitel;
    }
    const subtitel1 = getKZValueDisplay(row, "subtitle1", uiData.columns);
    const cost = getKZValue(row, "subtitle1", uiData.columns);
    sumCost.value += cost;
    const summe = toNumber(sumCost.value);
    const summe2 = numberToStringWithThousends(summe, 2);
    sumCost1.value = summe2;
    let text = getKZValueDisplay(row, "text", uiData.columns);
    if (text) {
      const textTitel = getKZTitle(row, "text", uiData.columns);
      text = textTitel + ": " + text;
    }
    let icon = getKZValueDisplay(row, "icon", uiData.columns);
    let select = false;
    const selectAic = getKZTitle(row, "bezahlt", uiData.columns);
    if (selectAic) {
      select = false;
    }
    let noDelete = false;
    const allowDelete = getKZValue(row, "allowDelete", uiData.columns);
    if (!allowDelete) {
      noDelete = true;
    }
    let pdf = getKZValue(row, "pdf", uiData.columns);
    let memo = "";
    let checkbox = false;
    if (pdf && pdf.name && pdf.type !== "application/pdf") {
      memo = pdf.name;
      icon = pdf;
      pdf = loadImage(pdf);
      itemHeight.value = "120";
    } else {
      checkbox = true;
    }
    const ampel = getKZValue(row, "ampel", uiData.columns);
    let color = "grey";
    if (ampel === 0) {
      color = "grey";
    } else if (ampel === 1) {
      color = "green";
    } else if (ampel === 2) {
      color = "orange";
    } else if (ampel === 3) {
      color = "red";
    }
    items.value.push({
      header,
      title,
      title2,
      subtitel,
      subtitel1,
      text,
      icon,
      differenz,
      divider: true,
      inset: true,
      aic: row.aic_Bew_pool ?? row.aic_Stamm,
      menu: false,
      select,
      pdf,
      memo,
      checkbox,
      ampel,
      noDelete,
      color,
    });
  }
}
function loadImage(image: any) {
  if (image.datentyp === "Doku") {
    const base64data = AUApi.Current.fullUrl(
      "pdf2/" + AUApi.Current.user.id + ":" + +image.aic + "_" + image.name,
    );
    return base64data;
  } else {
    const base64data = AUApi.Current.fullUrl(
      "image2/" + AUApi.Current.user.id + ":" + +image.aic + "_" + image.name,
    );
    return base64data;
  }
}
function onShowPdf(pdf: any) {
  showPDF2(pdf.name, pdf.aic, AUApi.Current.user.id);
}
function onShowImage(image: any) {
  showImage(image, image.aic, AUApi.Current.user.id);
}
function openEdit(aicBewegung: number) {
  let showFullscreen = false;
  if (Constants.isMobile && isPhone()) {
    showFullscreen = true;
  }
  let bewegungsdaten = true;
  let editQuery = "Web_NewManKostensatz";
  let stammQuery = "";
  let auswahlQuery = "";
  let neuDialog = "";
  if (belegeAzure.value) {
    editMask = "BaseStammMaske";
    const formular = globalsKennung(Globals.Begriff, "Web_RK_Stammdaten_Beleg");
    stammQuery = formular.stammQuery ?? "";
    auswahlQuery = formular.auswahlQuery ?? "";
    neuDialog = formular.neuDialog ?? "";
    editQuery = formular.query;
    // editQuery = "Web_EditBeleg_Belegerkennung";
    bewegungsdaten = false;
    showFullscreen = true;
  }
  showDialog({
    title: "bearbeiten",
    site: editMask,
    width: 400,
    fullscreen: showFullscreen,
    data: {
      zeitbereich: pageParams.zeitbereich,
      aicBewegung,
      aic: aicBewegung,
      bewegungsdaten,
      showDelete: true,
      editQuery,
      aicReise: props.aic,
      belegeAzure,
      stammQuery,
      auswahlQuery,
      neuDialog,
      neuErfassung: false,
    },
    onClose: (x: DialogParameter) => {
      if (x.returnedData || x.data.aicBewegung) {
        reloadAll();
        return true;
      } else {
        return true;
      }
    },
  });
}
async function reloadAll() {
  uiData.initialized = false;
  await reloadData(uiData, pageParams);
  await refreshItems();
  uiData.initialized = true;
}
function openNew(bewegungsdaten: boolean) {
  let showFullscreen = false;
  if (Constants.isMobile && isPhone()) {
    showFullscreen = true;
  }

  let editQuery = "Web_NewManKostensatz";
  if (belegeAzure.value) {
    editQuery = "Web_neuerBelegReisen_Azure";
  }
  const title = "neu";
  const updateData = undefined;
  showDialog({
    title,
    site: editMask,
    width: 400,
    fullscreen: showFullscreen,
    data: {
      zeitbereich: pageParams.zeitbereich,
      aicBewegung: "0",
      aicReise: props.aic,
      bewegungsdaten,
      editQuery,
      belegeAzure: belegeAzure.value,
      newData: true,
      updateData,
    },
    onClose: (x: DialogParameter) => {
      reloadAll();
      return true;
    },
  });
}
async function onDeleteAndRefresh(aic: number) {
  await onDelete(uiData, pageParams, aic, "", "");
  await reloadAll();
}
</script>
<template>
  <v-sheet class="pa-0" :min-height="400" v-if="uiData.initialized">
    <v-card :loading="uiData.showLoading" tile class="pa-0">
      <v-list min-heigth="400">
        <v-list-item>
          <v-list-item-subtitle style="text-align: left"
            >{{
              globalsBezeichnung(
                Globals.Begriff,
                "Web_angefalleneKosten",
                "angefallene Kosten",
              )
            }}:</v-list-item-subtitle
          >

          <template v-slot:append>
            <v-list-item-subtitle style="color: red"
              >{{ sumCost1 }} €
            </v-list-item-subtitle>
          </template>
        </v-list-item>
        <v-divider color="red"></v-divider>

        <v-virtual-scroll
          v-if="items && items.length > 0"
          benched="0"
          :items="items"
          height="400"
          :item-height="itemHeight"
        >
          <template v-slot:default="{ item }">
            <v-list-item ref="liste" :key="item.title" style="text-align: left">
              <template v-slot:prepend>
                <v-icon v-if="item.ampel" size="x-large" :color="item.color"
                  >mdi-circle-slice-8</v-icon
                >
              </template>
              <v-list-item-subtitle>{{ item.title }}</v-list-item-subtitle>
              <v-list-item-subtitle>{{ item.title2 }}</v-list-item-subtitle>
              <v-list-item-subtitle>{{ item.text }}</v-list-item-subtitle>
              <v-list-item-action class="align-end">
                <v-spacer></v-spacer>

                <v-btn
                  v-if="item.pdf && item.checkbox"
                  @click="onShowPdf(item.pdf)"
                  icon="mdi-file-pdf-box"
                  size="x-large"
                  variant="text"
                >
                  <v-icon>mdi-file-pdf-box</v-icon></v-btn
                >
                <v-img
                  v-if="item.memo"
                  :src="item.pdf"
                  style="max-width: 90%; max-height: 80px"
                  @click="onShowImage(item.icon)"
                  :aspect-ratio="16 / 9"
                  contain
                  class="my-2"
                />
                <v-btn
                  v-if="edit || belegeAzure"
                  variant="text"
                  size="small"
                  @click.prevent.stop="openEdit(item.aic ?? 0)"
                >
                  <v-icon color="success" small>mdi-pencil</v-icon>
                </v-btn>
                <v-btn
                  v-if="belegeAzure && item.aic && !item.noDelete"
                  variant="text"
                  size="small"
                  @click.prevent.stop="onDeleteAndRefresh(item.aic ?? 0)"
                >
                  <v-icon color="error" small>mdi-delete</v-icon>
                </v-btn>
                <v-list-item-subtitle style="color: red"
                  >{{ item.subtitel1 }} €
                </v-list-item-subtitle>
              </v-list-item-action>
            </v-list-item>
            <v-divider :inset="true"></v-divider>
          </template>
        </v-virtual-scroll>
      </v-list>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          v-if="belegeAzure"
          color="info"
          size="small"
          variant="elevated"
          @click="loadData()"
          icon="mdi-refresh"
        >
        </v-btn>
        <v-btn
          v-if="edit || belegeAzure"
          color="info"
          size="small"
          variant="elevated"
          @click="openNew(false)"
          icon="mdi-plus"
        >
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-sheet>
</template>

<style scoped>
.scroll {
  overflow-y: auto;
}
.v-list-item__content {
  align-items: left;
  align-content: flex-start;
}
.v-list-item__title {
  align-self: left;
}
</style>
